import { Component, OnInit } from '@angular/core'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { AdminService } from '../../services/admin.service'
import { HttpStatusCode } from '@angular/common/http'
import { MatDialog } from '@angular/material/dialog'
import { CallerService } from '../../services/caller.service'
import { PageEvent } from '@angular/material/paginator'
import { ConfirmActionDialogComponent } from '../../dialogs/confirm-action-dialog/confirm-action-dialog.component'

@Component({
  selector: 'app-admin-users',
  templateUrl: './admin-users.component.html',
  styleUrls: ['../admin-table.css', './admin-users.component.css'],
})
export class AdminUsersComponent implements OnInit {
  users: any[] = []
  blocked: any[] = []
  deleted: any[] = []
  loading = false
  loadingBlocked = false
  loadingDeleted = false
  page = 1
  limit = 10
  totalUsers: number | null = null
  blockedPage = 1
  blockedLimit = 10
  totalBlocked: number | null = null
  deletedPage = 1
  deletedLimit = 10
  totalDeleted: number | null = null
  readonly pageSizeOptions = [10, 20, 50, 100]
  searchControl = new FormControl('', [])
  stateCodeControl = new FormControl('', [])
  stateControl = new FormControl<number | string | ''>('', [])
  lgaControl = new FormControl<number | string | ''>('', [])
  stateOptions: Array<{ id: number | string; name: string }> = []
  lgaOptions: Array<{ id: number | string; name: string }> = []
  blockForm = new FormGroup({
    email: new FormControl('', []),
    state_code: new FormControl('', []),
    reason: new FormControl('', []),
  })
  showBlockForm = false
  blocking = false

  constructor(
    public adminService: AdminService,
    private dialog: MatDialog,
    private callerService: CallerService
  ) {}

  ngOnInit(): void {
    this.loadStateOptions()
    this.loadUsers()
    this.loadBlocked()
    this.loadDeleted()
    this.searchControl.valueChanges.subscribe(() => {
      this.page = 1
      this.loadUsers()
      this.deletedPage = 1
      this.loadDeleted()
    })
    this.stateCodeControl.valueChanges.subscribe(() => {
      this.page = 1
      this.loadUsers()
    })
    this.stateControl.valueChanges.subscribe((stateId) => {
      this.lgaControl.setValue('', { emitEvent: false })
      this.loadLgaOptions(stateId)
      this.page = 1
      this.loadUsers()
    })
    this.lgaControl.valueChanges.subscribe(() => {
      this.page = 1
      this.loadUsers()
    })
  }

  private loadStateOptions(): void {
    this.callerService.fetchAllNigeriaStates().subscribe({
      next: (res: any) => {
        if (res.status === HttpStatusCode.Ok) {
          this.stateOptions = (res.body?.states ?? []).map((s: any) => ({ id: s.id, name: s.name }))
        }
      },
      error: () => {
        this.adminService.showNotification('Failed to load states', 4000, 'Close')
      },
    })
  }

  private loadLgaOptions(stateId: number | string | '' | null | undefined): void {
    if (!stateId) {
      this.lgaOptions = []
      return
    }

    const numericStateId = Number(stateId)
    if (!Number.isFinite(numericStateId)) {
      this.lgaOptions = []
      return
    }

    this.callerService.fetchNigeriaStateLGAs(numericStateId).subscribe({
      next: (res: any) => {
        if (res.status === HttpStatusCode.Ok) {
          this.lgaOptions = (res.body?.lgas ?? []).map((lga: any) => ({ id: lga.id, name: lga.name }))
        }
      },
      error: () => {
        this.lgaOptions = []
        this.adminService.showNotification('Failed to load LGAs', 4000, 'Close')
      },
    })
  }

  private getSelectedStateName(): string | undefined {
    const selectedStateId = this.stateControl.value
    if (!selectedStateId) return undefined
    return this.stateOptions.find((s) => String(s.id) === String(selectedStateId))?.name
  }

  loadUsers(): void {
    this.loading = true
    const search = this.searchControl.value?.trim() || undefined
    const state_code = this.stateCodeControl.value?.trim() || undefined
    const state = this.getSelectedStateName()
    const lga_id = this.lgaControl.value ? String(this.lgaControl.value) : undefined
    this.adminService.getUsers({ page: this.page, limit: this.limit, search, state_code, state, lga_id }).subscribe({
      next: (res: any) => {
        this.loading = false
        if (res.status === HttpStatusCode.Ok && res.body) {
          this.users = Array.isArray(res.body) ? res.body : res.body.users ?? res.body.data ?? []
          this.totalUsers = res.body.total ?? res.body.count ?? this.users.length
        }
      },
      error: () => {
        this.loading = false
        this.adminService.showNotification('Failed to load users', 5000, 'Close')
      },
    })
  }

  loadBlocked(): void {
    this.loadingBlocked = true
    this.adminService.getBlockedUsers({ page: this.blockedPage, limit: this.blockedLimit }).subscribe({
      next: (res: any) => {
        this.loadingBlocked = false
        if (res.status === HttpStatusCode.Ok && res.body) {
          this.blocked = Array.isArray(res.body) ? res.body : res.body.blocked ?? res.body.data ?? []
          this.totalBlocked = res.body.total ?? res.body.count ?? this.blocked.length
        }
      },
      error: () => {
        this.loadingBlocked = false
        this.adminService.showNotification('Failed to load blocked users', 5000, 'Close')
      },
    })
  }

  loadDeleted(): void {
    this.loadingDeleted = true
    const search = this.searchControl.value?.trim() || undefined
    this.adminService.getDeletedUsers({ page: this.deletedPage, limit: this.deletedLimit, search }).subscribe({
      next: (res: any) => {
        this.loadingDeleted = false
        if (res.status === HttpStatusCode.Ok && res.body) {
          this.deleted = Array.isArray(res.body) ? res.body : res.body.deleted ?? res.body.data ?? []
          this.totalDeleted = res.body.total ?? res.body.count ?? this.deleted.length
        }
      },
      error: () => {
        this.loadingDeleted = false
        this.adminService.showNotification('Failed to load deleted users', 5000, 'Close')
      },
    })
  }

  onUsersPageChange(event: PageEvent): void {
    this.limit = event.pageSize
    this.page = event.pageIndex + 1
    this.loadUsers()
  }

  onBlockedPageChange(event: PageEvent): void {
    this.blockedLimit = event.pageSize
    this.blockedPage = event.pageIndex + 1
    this.loadBlocked()
  }

  onDeletedPageChange(event: PageEvent): void {
    this.deletedLimit = event.pageSize
    this.deletedPage = event.pageIndex + 1
    this.loadDeleted()
  }

  deleteUser(user: any): void {
    const id = user.id ?? user._id
    if (!id) return
    const display = this.userDisplay(user)
    const ref = this.dialog.open(ConfirmActionDialogComponent, {
      width: '420px',
      data: {
        title: 'Delete user?',
        message: `Delete ${display}? This action cannot be undone.`,
        confirmText: 'Delete',
        confirmColor: 'warn',
        askReason: true,
        reasonRequired: true,
        reasonLabel: 'Reason for deleting',
        reasonPlaceholder: 'Why is this user being deleted?',
      },
    })
    ref.afterClosed().subscribe((result) => {
      if (!result?.confirmed) return
      this.adminService.deleteUser(id, result.reason).subscribe({
        next: (res: any) => {
          if (res.status === HttpStatusCode.Ok) {
            this.adminService.showNotification('User deleted', 3000, 'OK')
            this.loadUsers()
            this.loadDeleted()
          }
        },
        error: (err) => {
          this.adminService.showNotification(err?.error?.message ?? 'Failed to delete user', 5000, 'Close')
        },
      })
    })
  }

  blockFromTable(user: any): void {
    const email = user?.email?.trim?.()
    const state_code = user?.state_code?.trim?.()
    if (!email && !state_code) {
      this.adminService.showNotification('User has no email/state code to block', 4000, 'Close')
      return
    }

    const display = this.userDisplay(user)
    const ref = this.dialog.open(ConfirmActionDialogComponent, {
      width: '420px',
      data: {
        title: 'Block user?',
        message: `Block ${display}? They will be moved to blocked users.`,
        confirmText: 'Block',
        confirmColor: 'warn',
        askReason: true,
        reasonRequired: true,
        reasonLabel: 'Reason for blocking',
        reasonPlaceholder: 'Why is this user being blocked?',
      },
    })
    ref.afterClosed().subscribe((result) => {
      if (!result?.confirmed) return
      this.adminService.blockUser({ email: email || undefined, state_code: state_code || undefined, reason: result.reason }).subscribe({
        next: (res: any) => {
          if (res.status === HttpStatusCode.Created || res.status === HttpStatusCode.Ok) {
            this.adminService.showNotification('User blocked', 3000, 'OK')
            this.loadUsers()
            this.loadBlocked()
          }
        },
        error: (err) => {
          this.adminService.showNotification(err?.error?.message ?? 'Failed to block user', 5000, 'Close')
        },
      })
    })
  }

  openBlockForm(): void {
    this.showBlockForm = true
    this.blockForm.reset()
  }

  closeBlockForm(): void {
    this.showBlockForm = false
  }

  submitBlock(): void {
    const email = this.blockForm.get('email')?.value?.trim()
    const state_code = this.blockForm.get('state_code')?.value?.trim()
    const reason = this.blockForm.get('reason')?.value?.trim()
    if (!email && !state_code) {
      this.adminService.showNotification('Enter email or state code', 4000, 'Close')
      return
    }
    this.blocking = true
    this.adminService.blockUser({ email: email || undefined, state_code: state_code || undefined, reason: reason || undefined }).subscribe({
      next: (res: any) => {
        this.blocking = false
        if (res.status === HttpStatusCode.Created || res.status === HttpStatusCode.Ok) {
          this.adminService.showNotification('User blocked', 3000, 'OK')
          this.closeBlockForm()
          this.loadUsers()
          this.loadBlocked()
        }
      },
      error: (err) => {
        this.blocking = false
        this.adminService.showNotification(err?.error?.message ?? 'Failed to block user', 5000, 'Close')
      },
    })
  }

  unblock(entry: any): void {
    const id = entry.id ?? entry._id
    if (!id) return
    this.adminService.unblockUser(id).subscribe({
      next: (res: any) => {
        if (res.status === HttpStatusCode.Ok) {
          this.adminService.showNotification('User unblocked', 3000, 'OK')
          this.loadBlocked()
        }
      },
      error: (err) => {
        this.adminService.showNotification(err?.error?.message ?? 'Failed to unblock', 5000, 'Close')
      },
    })
  }

  userDisplay(u: any): string {
    return u.email ?? u.state_code ?? u.nickname ?? `#${u.id ?? u._id}`
  }
}
